import { useCallback, useEffect, useRef, useState } from "react";

import { msg } from "@lingui/core/macro";
import { Plural, Trans, useLingui } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { cn } from "@pontimed/ui";
import { AnimatePresence, m } from "framer-motion";
import {
  Activity,
  Bell,
  BookOpen,
  Calendar,
  ChevronDown,
  ChevronUp,
  Clock,
  Download,
  FileText,
  Filter,
  ImageIcon,
  Loader2,
  Paperclip,
  Pencil,
  Pill,
  Pin,
  Plus,
  RotateCcw,
  Search,
  Stethoscope,
  Syringe,
  Tag,
  TestTube,
  Trash2,
  X,
} from "lucide-react";

import { showToast } from "@/lib/ui/toast";
import { handleVaultLocked, isVaultLockedResponse, reportVaultLocked } from "@/lib/vault-unlock";
import { Button } from "@/components/ui/button";
import { ConfirmSheet } from "@/components/ui/confirm-sheet";
import { LoadErrorState } from "@/components/ui/load-error-state";
import { AccountSecurityBanner } from "@/components/account/account-security-banner";

import { HealthMetricsChart } from "./health-metrics-chart";
import { JournalEntryModal } from "./journal-entry-modal";
import { LabTrendsChart } from "./lab-trends-chart";

/** Snooze duration options in days */
const SNOOZE_OPTIONS = [
  { key: "1d", days: 1 },
  { key: "3d", days: 3 },
  { key: "1w", days: 7 },
  { key: "1m", days: 30 },
] as const;

/* ─── Types ─── */

export interface JournalEntryData {
  id: string;
  type: string;
  title: string;
  notes: string | null;
  eventDate: string;
  tags: string[];
  status: string;
  isPinned: boolean;
  metadata: Record<string, unknown> | null;
  providerId: string | null;
  createdAt: string;
  _count: { attachments: number; reminders: number };
  reminders: { id: string; remindAt: string; message: string; status: string }[];
}

const ENTRY_TYPE_ICONS: Record<string, typeof FileText> = {
  visit: Stethoscope,
  document: FileText,
  lab_result: TestTube,
  imaging: ImageIcon,
  prescription: Pill,
  note: Pencil,
  vaccine: Syringe,
  reminder: Bell,
  metric: Activity,
};

const ENTRY_TYPE_COLORS: Record<string, string> = {
  visit: "bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-400",
  document: "bg-purple-50 text-purple-600 dark:bg-purple-950 dark:text-purple-400",
  lab_result: "bg-green-50 text-green-600 dark:bg-green-950 dark:text-green-400",
  imaging: "bg-sky-50 text-sky-600 dark:bg-sky-950 dark:text-sky-400",
  prescription: "bg-teal-50 text-teal-600 dark:bg-teal-950 dark:text-teal-400",
  note: "bg-amber-50 text-amber-600 dark:bg-amber-950 dark:text-amber-400",
  vaccine: "bg-indigo-50 text-indigo-600 dark:bg-indigo-950 dark:text-indigo-400",
  reminder: "bg-rose-50 text-rose-600 dark:bg-rose-950 dark:text-rose-400",
  metric: "bg-emerald-50 text-emerald-600 dark:bg-emerald-950 dark:text-emerald-400",
};

const ALL_ENTRY_TYPES = [
  "visit",
  "document",
  "lab_result",
  "imaging",
  "prescription",
  "note",
  "vaccine",
  "reminder",
  "metric",
] as const;

const PERIOD_OPTIONS = [
  { key: "any", days: 0 },
  { key: "7d", days: 7 },
  { key: "30d", days: 30 },
  { key: "3m", days: 90 },
  { key: "6m", days: 180 },
  { key: "1y", days: 365 },
] as const;

type PeriodKey = (typeof PERIOD_OPTIONS)[number]["key"];

function getEntryTypeLabel(type: string, t: ReturnType<typeof useLingui>["t"]): string {
  switch (type) {
    case "visit":
      return t(msg`Visit`);
    case "document":
      return t(msg`Document`);
    case "lab_result":
      return t(msg`Lab result`);
    case "imaging":
      return t(msg`Imaging`);
    case "prescription":
      return t(msg`Prescription`);
    case "note":
      return t(msg`Note`);
    case "vaccine":
      return t(msg`Vaccine`);
    case "reminder":
      return t(msg`Reminder`);
    case "metric":
      return t(msg`Metric`);
    default:
      return type;
  }
}

function getPeriodLabel(key: PeriodKey, t: ReturnType<typeof useLingui>["t"]): string {
  switch (key) {
    case "any":
      return t(msg`Any period`);
    case "7d":
      return t(msg`Last 7 days`);
    case "30d":
      return t(msg`Last 30 days`);
    case "3m":
      return t(msg`Last 3 months`);
    case "6m":
      return t(msg`Last 6 months`);
    case "1y":
      return t(msg`Last year`);
  }
}

function getSnoozeLabel(
  key: (typeof SNOOZE_OPTIONS)[number]["key"],
  t: ReturnType<typeof useLingui>["t"],
): string {
  switch (key) {
    case "1d":
      return t(msg`Snooze 1 day`);
    case "3d":
      return t(msg`Snooze 3 days`);
    case "1w":
      return t(msg`Snooze 1 week`);
    case "1m":
      return t(msg`Snooze 1 month`);
  }
}

interface JournalListResponse {
  entries: JournalEntryData[];
  total: number;
}

interface BillingSubscription {
  planTier?: string;
}

interface BillingResponse {
  subscriptions?: BillingSubscription[];
  /** Legacy shape kept for tolerance — apps/api returns `subscriptions` plural. */
  subscription?: BillingSubscription;
  planTier?: string;
}

interface VaultMemberLite {
  id: string;
  name: string;
  relationship: string;
}

interface VaultMembersResponse {
  members?: VaultMemberLite[];
}

/* ─── Main Component ─── */

export function JournalTab({ activeMemberId }: { activeMemberId?: string | null }) {
  const { t } = useLingui();
  const [entries, setEntries] = useState<JournalEntryData[]>([]);
  const [loading, setLoading] = useState(true);
  /** The list could not load — never shown as "your journal is empty" (P16). */
  const [loadFailed, setLoadFailed] = useState(false);
  const [_total, setTotal] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState<string>("note");
  const [editingEntry, setEditingEntry] = useState<JournalEntryData | null>(null);
  const [planTier, setPlanTier] = useState("free");
  const [vaultMembers, setVaultMembers] = useState<VaultMemberLite[]>([]);

  // Fetch user plan tier + vault members
  useEffect(() => {
    apiFetch
      .get<BillingResponse>("/api/account/billing")
      .then((data) => {
        const fromArray = data?.subscriptions?.[0]?.planTier;
        if (fromArray) setPlanTier(fromArray);
        else if (data?.subscription?.planTier) setPlanTier(data.subscription.planTier);
        else if (data?.planTier) setPlanTier(data.planTier);
      })
      .catch(() => {});
    apiFetch
      .get<VaultMembersResponse | VaultMemberLite[]>("/api/account/vault/members")
      .then((data) => {
        if (Array.isArray(data)) {
          setVaultMembers(data);
        } else if (data && Array.isArray(data.members)) {
          setVaultMembers(
            data.members.map((m) => ({
              id: m.id,
              name: m.name,
              relationship: m.relationship,
            })),
          );
        }
      })
      .catch((err: unknown) => {
        // Locked meanwhile: the PIN gate comes back. Otherwise no members.
        handleVaultLocked(err);
      });
  }, []);

  // Filter state
  const [filterTypes, setFilterTypes] = useState<string[]>([]);
  const [filterPeriod, setFilterPeriod] = useState<PeriodKey>("any");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Debounce search input
  useEffect(() => {
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(searchTimerRef.current);
  }, [searchQuery]);

  const hasActiveFilters =
    filterTypes.length > 0 || filterPeriod !== "any" || debouncedSearch !== "";

  const clearAllFilters = useCallback(() => {
    setFilterTypes([]);
    setFilterPeriod("any");
    setSearchQuery("");
    setDebouncedSearch("");
  }, []);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "50", sort: "eventDate:desc" });

      // Pass vault member filter
      if (activeMemberId) {
        params.set("memberId", activeMemberId);
      }

      // Apply filter bar params
      if (filterTypes.length > 0) {
        params.set("type", filterTypes.join(","));
      }
      if (filterPeriod !== "any") {
        const opt = PERIOD_OPTIONS.find((p) => p.key === filterPeriod);
        if (opt && opt.days > 0) {
          const from = new Date();
          from.setDate(from.getDate() - opt.days);
          params.set("from", from.toISOString());
        }
      }
      if (debouncedSearch) {
        params.set("search", debouncedSearch);
      }

      const data = await apiFetch.get<JournalListResponse>(`/api/account/journal?${params}`);
      setEntries(data.entries);
      setTotal(data.total);
      setLoadFailed(false);
    } catch (err) {
      // Locked meanwhile: the PIN gate comes back. Otherwise keep what is on
      // screen and flag the failure, so an empty list shows the error card.
      if (!handleVaultLocked(err)) setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [activeMemberId, filterTypes, filterPeriod, debouncedSearch]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const handleDelete = useCallback(
    async (id: string) => {
      try {
        await apiFetch.delete(`/api/account/journal/${id}`);
        setEntries((prev) => prev.filter((e) => e.id !== id));
        setTotal((prev) => prev - 1);
        showToast(t`Entry deleted`, "success");
      } catch (err) {
        if (!handleVaultLocked(err)) showToast(t`Could not delete the entry. Try again.`, "error");
      }
    },
    [t],
  );

  const handleDownloadIcs = useCallback(async (entryId: string) => {
    try {
      const res = await apiFetch.raw(`/api/account/journal/${entryId}/reminder/ics`, {
        method: "GET",
      });
      if (await isVaultLockedResponse(res)) {
        reportVaultLocked();
        return;
      }
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "reminder.ics";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // Silent — download failed.
    }
  }, []);

  const openAddModal = useCallback((type: string) => {
    setEditingEntry(null);
    setModalType(type);
    setModalOpen(true);
  }, []);

  const openEditModal = useCallback((entry: JournalEntryData) => {
    setEditingEntry(entry);
    setModalType(entry.type);
    setModalOpen(true);
  }, []);

  const handleSaved = useCallback(() => {
    setModalOpen(false);
    setEditingEntry(null);
    fetchEntries();
  }, [fetchEntries]);

  const pinned = entries.filter((e) => e.isPinned);
  const unpinned = entries.filter((e) => !e.isPinned);

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Add entry button — first */}
        <Button className="min-h-[44px] gap-1.5" onClick={() => openAddModal("note")}>
          <Plus className="size-4" />
          <Trans>Add entry</Trans>
        </Button>

        {/* Search */}
        <div className="border-border bg-card flex min-h-[44px] min-w-[140px] flex-1 items-center gap-2 rounded-lg border px-3 sm:max-w-[220px] sm:flex-none">
          <Search className="text-muted-foreground size-4 shrink-0" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t`Search...`}
            className="text-foreground placeholder:text-muted-foreground w-full bg-transparent text-sm outline-none"
          />
          {searchQuery && (
            <button
              onClick={() => {
                setSearchQuery("");
                setDebouncedSearch("");
              }}
              className="text-muted-foreground hover:text-foreground shrink-0"
              aria-label={t`Clear search`}
            >
              <X className="size-3.5" />
            </button>
          )}
        </div>

        {/* Type filter */}
        <div className="relative">
          <select
            value={filterTypes.length === 0 ? "" : filterTypes.join(",")}
            onChange={(e) => {
              const val = e.target.value;
              setFilterTypes(val === "" ? [] : [val]);
            }}
            className={cn(
              "border-border bg-card text-foreground focus:ring-primary/30 focus:border-primary min-h-[44px] cursor-pointer appearance-none rounded-lg border pr-8 pl-3 text-sm outline-none focus:ring-2 focus:ring-inset",
              filterTypes.length > 0 && "border-primary bg-primary/5 font-medium",
            )}
            aria-label={t`Filter by type`}
          >
            <option value="">{t`Type`}</option>
            {ALL_ENTRY_TYPES.map((type) => (
              <option key={type} value={type}>
                {getEntryTypeLabel(type, t)}
              </option>
            ))}
          </select>
          <ChevronDown className="text-muted-foreground pointer-events-none absolute top-1/2 right-2 size-4 -translate-y-1/2" />
        </div>

        {/* Period filter */}
        <div className="relative">
          <select
            value={filterPeriod}
            onChange={(e) => setFilterPeriod(e.target.value as PeriodKey)}
            className={cn(
              "border-border bg-card text-foreground focus:ring-primary/30 focus:border-primary min-h-[44px] cursor-pointer appearance-none rounded-lg border pr-8 pl-3 text-sm outline-none focus:ring-2 focus:ring-inset",
              filterPeriod !== "any" && "border-primary bg-primary/5 font-medium",
            )}
            aria-label={t`Filter by period`}
          >
            {PERIOD_OPTIONS.map((opt) => (
              <option key={opt.key} value={opt.key}>
                {opt.key === "any" ? t`Period` : getPeriodLabel(opt.key, t)}
              </option>
            ))}
          </select>
          <ChevronDown className="text-muted-foreground pointer-events-none absolute top-1/2 right-2 size-4 -translate-y-1/2" />
        </div>

        {/* Clear all — only when filters active */}
        {hasActiveFilters && (
          <button
            onClick={clearAllFilters}
            className="text-primary hover:text-primary/80 flex min-h-[44px] items-center gap-1.5 px-2 text-sm font-medium"
          >
            <X className="size-3.5" />
            <Trans>Clear</Trans>
          </button>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <JournalSkeleton />
      ) : loadFailed && entries.length === 0 ? (
        <LoadErrorState
          title={<Trans>We couldn't load your journal.</Trans>}
          description={<Trans>Your entries are safe. Check your connection and try again.</Trans>}
          onRetry={() => void fetchEntries()}
        />
      ) : entries.length === 0 && !hasActiveFilters ? (
        <EmptyJournal onAdd={() => openAddModal("note")} />
      ) : entries.length === 0 && hasActiveFilters ? (
        <div className="border-border bg-card rounded-xl border p-5">
          <div className="flex flex-col items-center justify-center py-12">
            <div className="bg-muted flex size-16 items-center justify-center rounded-full">
              <Filter className="text-muted-foreground size-8" />
            </div>
            <h3 className="text-foreground mt-4 text-sm font-semibold">
              <Trans>No results</Trans>
            </h3>
            <Button variant="outline" className="mt-4 min-h-[44px]" onClick={clearAllFilters}>
              <X className="mr-1.5 size-4" />
              <Trans>Clear filters</Trans>
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Lab Trends & Health Metrics Charts */}
          <LabTrendsChart entries={entries} />
          <HealthMetricsChart entries={entries} />

          {/* Pinned entries */}
          {pinned.length > 0 && (
            <div className="space-y-2">
              <p className="text-muted-foreground flex items-center gap-1.5 text-xs font-medium tracking-wide uppercase">
                <Pin className="size-3" />
                <Trans>Pinned</Trans>
              </p>
              {pinned.map((entry, i) => (
                <EntryCard
                  key={entry.id}
                  entry={entry}
                  index={i}
                  onDelete={handleDelete}
                  onEdit={openEditModal}
                  onDownloadIcs={handleDownloadIcs}
                  onReminderUpdate={fetchEntries}
                />
              ))}
            </div>
          )}

          {/* Regular entries */}
          {unpinned.map((entry, i) => (
            <EntryCard
              key={entry.id}
              entry={entry}
              index={i}
              onDelete={handleDelete}
              onEdit={openEditModal}
              onDownloadIcs={handleDownloadIcs}
            />
          ))}
        </div>
      )}

      <AccountSecurityBanner />

      {/* Entry modal */}
      <JournalEntryModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        entryType={modalType}
        editingEntry={editingEntry}
        onSaved={handleSaved}
        planTier={planTier}
        activeMemberId={activeMemberId}
        vaultMembers={vaultMembers}
      />
    </div>
  );
}

/* ─── Attachment Types ─── */

interface AttachmentData {
  id: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  fileUrl: string;
  thumbnailUrl: string | null;
}

function isImageType(fileType: string) {
  return fileType.startsWith("image/");
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/* ─── Entry Card ─── */

function EntryCard({
  entry,
  index,
  onDelete,
  onEdit,
  onDownloadIcs,
  onReminderUpdate,
}: {
  entry: JournalEntryData;
  index: number;
  onDelete: (id: string) => Promise<void>;
  onEdit: (entry: JournalEntryData) => void;
  onDownloadIcs: (entryId: string) => void;
  onReminderUpdate?: () => void;
}) {
  const { t, i18n } = useLingui();
  const locale = i18n.locale;
  const [expanded, setExpanded] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [snoozeOpenId, setSnoozeOpenId] = useState<string | null>(null);
  const [updatingReminder, setUpdatingReminder] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<AttachmentData[]>([]);
  const [attachmentsLoaded, setAttachmentsLoaded] = useState(false);

  // Fetch attachments when expanded and entry has attachments
  useEffect(() => {
    if (!expanded || attachmentsLoaded || entry._count.attachments === 0) return;
    (async () => {
      try {
        const data = await apiFetch.get<AttachmentData[]>(
          `/api/account/journal/${entry.id}/attachments`,
        );
        setAttachments(data);
      } catch (err) {
        // Locked meanwhile: the PIN gate comes back. Otherwise silent — keep empty list.
        handleVaultLocked(err);
      } finally {
        setAttachmentsLoaded(true);
      }
    })();
  }, [expanded, attachmentsLoaded, entry.id, entry._count.attachments]);

  // Encrypted attachments come back with a RELATIVE /api/ fileUrl that must be
  // fetched with auth cookies. On portal.pontimed.app the SPA and the API live
  // on different origins, so a plain <a href="/api/…"> would hit the SPA origin
  // (404) and drop credentials. Stream the ciphertext through apiFetch (correct
  // origin + cookies), decrypt server-side, and open it from an object URL.
  // Absolute (legacy plaintext) URLs stay plain links.
  const openAttachment = useCallback(
    async (att: AttachmentData) => {
      if (!att.fileUrl.startsWith("/api/")) {
        window.open(att.fileUrl, "_blank", "noopener,noreferrer");
        return;
      }
      // Open the tab synchronously (inside the click gesture) so the popup
      // blocker doesn't kill it after the await.
      const win = window.open("", "_blank");
      try {
        const res = await apiFetch.raw(att.fileUrl, { method: "GET" });
        if (await isVaultLockedResponse(res)) {
          win?.close();
          reportVaultLocked();
          return;
        }
        if (!res.ok) throw new Error(`status ${res.status}`);
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        if (win) {
          win.location.href = url;
        } else {
          // Popup blocked — fall back to a download.
          const a = document.createElement("a");
          a.href = url;
          a.download = att.fileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } catch {
        win?.close();
        showToast(t`Could not open the attachment. Try again.`, "error");
      }
    },
    [t],
  );

  const Icon = ENTRY_TYPE_ICONS[entry.type] || FileText;
  const colorClass = ENTRY_TYPE_COLORS[entry.type] || ENTRY_TYPE_COLORS.note;
  const typeLabel = getEntryTypeLabel(entry.type, t);

  const date = new Date(entry.eventDate);
  const formattedDate = date.toLocaleDateString(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  const hasReminders = entry._count.reminders > 0;
  const attachmentCount = entry._count.attachments;
  const hasAttachments = attachmentCount > 0;
  const detailsId = `journal-entry-${entry.id}`;
  const overdueReminders = entry.reminders.filter(
    (r) => r.status === "pending" && new Date(r.remindAt) < new Date(),
  );

  const handleReminderAction = async (
    reminderId: string,
    action: "dismissed" | "snoozed" | "pending",
    snoozeDays?: number,
  ) => {
    setUpdatingReminder(reminderId);
    const toast = showToast(t`Saving…`, "loading");
    try {
      const body: Record<string, unknown> = { reminderId, status: action };
      if (action === "snoozed" && snoozeDays) {
        const snoozedUntil = new Date();
        snoozedUntil.setDate(snoozedUntil.getDate() + snoozeDays);
        body.remindAt = snoozedUntil.toISOString();
      }
      await apiFetch.put("/api/account/journal/reminders", body);
      toast.update(t`Saved`, "success");
      onReminderUpdate?.();
    } catch (err) {
      if (handleVaultLocked(err)) {
        toast.update(t`Unlock your medical vault to continue.`, "info");
      } else {
        toast.update(t`Could not update the reminder. Try again.`, "error");
      }
    } finally {
      setUpdatingReminder(null);
      setSnoozeOpenId(null);
    }
  };

  return (
    <m.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.03 }}
      className="border-border bg-card rounded-xl border"
    >
      {/* Header — the title is the real toggle button (keyboard, screen
          readers, aria-expanded); its ::after covers the header so a tap
          anywhere on it still expands the entry. */}
      <div className="relative flex items-start gap-3 p-4">
        <div
          className={cn("flex size-10 shrink-0 items-center justify-center rounded-lg", colorClass)}
        >
          <Icon className="size-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <button
                type="button"
                aria-expanded={expanded}
                aria-controls={detailsId}
                onClick={() => setExpanded((e) => !e)}
                className="text-foreground focus-visible:after:ring-ring/50 cursor-pointer text-left text-sm font-semibold outline-none after:absolute after:inset-0 after:rounded-xl after:content-[''] focus-visible:after:ring-3"
              >
                {entry.title}
              </button>
              <p className="text-muted-foreground text-xs">
                {typeLabel} &middot; {formattedDate}
              </p>
            </div>
            {entry.isPinned && <Pin className="text-primary mt-0.5 size-3.5 shrink-0" />}
          </div>

          {/* Tags */}
          {entry.tags.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {entry.tags.map((tag) => (
                <span
                  key={tag}
                  className="bg-muted text-muted-foreground inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs"
                >
                  <Tag className="size-2.5" />
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Notes preview */}
          {entry.notes && !expanded && (
            <p className="text-muted-foreground mt-1 line-clamp-2 text-xs">{entry.notes}</p>
          )}

          {/* Meta badges */}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {hasAttachments && (
              <span className="text-muted-foreground inline-flex items-center gap-1 text-xs">
                <Paperclip className="size-3" aria-hidden />
                <Plural value={attachmentCount} one="# attachment" other="# attachments" />
              </span>
            )}
            {hasReminders && (
              <span
                className={cn(
                  "inline-flex items-center gap-1 text-xs",
                  overdueReminders.length > 0
                    ? "font-medium text-red-600 dark:text-red-400"
                    : "text-muted-foreground",
                )}
              >
                <Bell className="size-3" />
                {overdueReminders.length > 0 ? (
                  <Trans>Overdue</Trans>
                ) : entry.reminders[0]?.remindAt ? (
                  new Date(entry.reminders[0].remindAt).toLocaleDateString(locale, {
                    day: "numeric",
                    month: "short",
                  })
                ) : (
                  <Trans>Pending</Trans>
                )}
              </span>
            )}
          </div>
        </div>
        <span className="text-muted-foreground mt-1 shrink-0" aria-hidden>
          {expanded ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
        </span>
      </div>

      {/* Expanded content */}
      <AnimatePresence>
        {expanded && (
          <m.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
            id={detailsId}
          >
            <div className="border-border space-y-3 border-t px-4 pt-3 pb-4">
              {/* Full notes */}
              {entry.notes && (
                <div className="bg-muted/50 rounded-lg p-3">
                  <p className="text-foreground text-sm whitespace-pre-wrap">{entry.notes}</p>
                </div>
              )}

              {/* Attachments */}
              {hasAttachments && (
                <div>
                  <p className="text-muted-foreground mb-2 flex items-center gap-1.5 text-xs font-medium">
                    <Paperclip className="size-3" />
                    <Trans>Attachments</Trans> ({entry._count.attachments})
                  </p>
                  {!attachmentsLoaded ? (
                    <div className="flex items-center gap-2 py-2">
                      <Loader2 className="text-muted-foreground size-4 animate-spin" />
                      <span className="text-muted-foreground text-xs">
                        <Trans>Loading attachments…</Trans>
                      </span>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                      {attachments.map((att) => {
                        const tileClass =
                          "border-border bg-muted/30 hover:bg-muted group flex flex-col items-center gap-1.5 rounded-lg border p-2 transition-colors";
                        const tileInner = (
                          <>
                            {isImageType(att.fileType) && att.thumbnailUrl ? (
                              <img
                                src={att.thumbnailUrl}
                                alt={att.fileName}
                                className="h-16 w-full rounded object-cover"
                                loading="lazy"
                              />
                            ) : (
                              <div className="flex h-16 w-full items-center justify-center rounded bg-purple-50 dark:bg-purple-950">
                                <FileText className="size-6 text-purple-500" />
                              </div>
                            )}
                            <div className="w-full min-w-0">
                              <p className="text-foreground truncate text-[10px] font-medium">
                                {att.fileName}
                              </p>
                              <p className="text-muted-foreground text-[10px]">
                                {formatFileSize(att.fileSize)}
                              </p>
                            </div>
                            <Download className="text-muted-foreground group-hover:text-primary size-3.5 opacity-0 transition-opacity group-hover:opacity-100" />
                          </>
                        );
                        // Encrypted attachments (relative /api/ URL) need an
                        // auth'd cross-origin fetch; plaintext (absolute) URLs
                        // stay plain links.
                        return att.fileUrl.startsWith("/api/") ? (
                          <button
                            key={att.id}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              void openAttachment(att);
                            }}
                            className={cn(tileClass, "text-left")}
                          >
                            {tileInner}
                          </button>
                        ) : (
                          <a
                            key={att.id}
                            href={att.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className={tileClass}
                          >
                            {tileInner}
                          </a>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Reminders detail */}
              {entry.reminders.length > 0 && (
                <div className="space-y-2">
                  {entry.reminders.map((rem) => {
                    const isOverdue =
                      rem.status === "pending" && new Date(rem.remindAt) < new Date();
                    const isDismissed = rem.status === "dismissed";
                    const isSnoozed = rem.status === "snoozed";
                    const isPending = rem.status === "pending";
                    const isUpdating = updatingReminder === rem.id;

                    return (
                      <div key={rem.id} className="space-y-1.5">
                        <div
                          className={cn(
                            "flex items-center gap-2 rounded-lg border p-2 text-xs",
                            isDismissed
                              ? "border-border bg-muted/20 opacity-60"
                              : isOverdue
                                ? "border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950"
                                : isSnoozed
                                  ? "border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950"
                                  : "border-border bg-muted/30",
                          )}
                        >
                          <Bell
                            className={cn(
                              "size-3.5 shrink-0",
                              isDismissed
                                ? "text-muted-foreground"
                                : isOverdue
                                  ? "text-red-500"
                                  : isSnoozed
                                    ? "text-amber-500"
                                    : "text-muted-foreground",
                            )}
                          />
                          <span
                            className={cn(
                              "flex-1 font-medium",
                              isDismissed
                                ? "text-muted-foreground line-through"
                                : "text-foreground",
                            )}
                          >
                            {rem.message}
                          </span>
                          <span className="text-muted-foreground shrink-0">
                            {new Date(rem.remindAt).toLocaleDateString(locale, {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            })}
                          </span>
                        </div>

                        {/* Reminder action buttons */}
                        <div className="flex flex-wrap items-center gap-1.5 pl-1">
                          {isPending && (
                            <>
                              <Button
                                variant="outline"
                                size="touch"
                                className="text-muted-foreground"
                                disabled={isUpdating}
                                onClick={() => handleReminderAction(rem.id, "dismissed")}
                              >
                                {isUpdating ? (
                                  <Loader2 className="size-3 animate-spin" />
                                ) : (
                                  <X className="size-3" />
                                )}
                                <Trans>Dismiss</Trans>
                              </Button>
                              <Button
                                variant="outline"
                                size="touch"
                                className="text-muted-foreground"
                                disabled={isUpdating}
                                aria-expanded={snoozeOpenId === rem.id}
                                aria-controls={`snooze-${rem.id}`}
                                onClick={() =>
                                  setSnoozeOpenId(snoozeOpenId === rem.id ? null : rem.id)
                                }
                              >
                                <Clock className="size-4" aria-hidden />
                                <Trans>Snooze</Trans>
                                {snoozeOpenId === rem.id ? (
                                  <ChevronUp className="size-4" aria-hidden />
                                ) : (
                                  <ChevronDown className="size-4" aria-hidden />
                                )}
                              </Button>
                            </>
                          )}
                          {isDismissed && (
                            <Button
                              variant="outline"
                              size="touch"
                              className="text-muted-foreground"
                              disabled={isUpdating}
                              onClick={() => handleReminderAction(rem.id, "pending")}
                            >
                              {isUpdating ? (
                                <Loader2 className="size-3 animate-spin" />
                              ) : (
                                <RotateCcw className="size-3" />
                              )}
                              <Trans>Restore</Trans>
                            </Button>
                          )}
                          {(isPending || isSnoozed) && (
                            <button
                              type="button"
                              onClick={() => onDownloadIcs(entry.id)}
                              className="text-primary hover:text-primary/80 inline-flex min-h-11 items-center gap-1 px-2 text-xs sm:pointer-fine:min-h-9"
                            >
                              <Download className="size-3.5" />
                              <Trans>Download .ics</Trans>
                            </button>
                          )}
                        </div>

                        {/* Snooze choices — an inline row of chips under the
                            reminder, so nothing is clipped by the expanded
                            panel's overflow (P20). Escape closes it. */}
                        {isPending && snoozeOpenId === rem.id && (
                          <div
                            id={`snooze-${rem.id}`}
                            role="group"
                            aria-label={t`Snooze for`}
                            className="flex flex-wrap gap-2 pl-1"
                            onKeyDown={(e) => {
                              if (e.key === "Escape") setSnoozeOpenId(null);
                            }}
                          >
                            {SNOOZE_OPTIONS.map((opt) => (
                              <button
                                key={opt.key}
                                type="button"
                                disabled={isUpdating}
                                onClick={() => handleReminderAction(rem.id, "snoozed", opt.days)}
                                className="border-border bg-card text-foreground hover:bg-muted focus-visible:ring-ring/50 inline-flex min-h-11 items-center rounded-full border px-3 text-xs font-medium outline-none focus-visible:ring-3 disabled:opacity-50 sm:pointer-fine:min-h-8"
                              >
                                {getSnoozeLabel(opt.key, t)}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="touch" onClick={() => onEdit(entry)}>
                  <Pencil className="size-4" aria-hidden />
                  <Trans>Edit</Trans>
                </Button>
                <Button
                  variant="destructive"
                  size="touch"
                  disabled={deleting}
                  onClick={() => setConfirmDelete(true)}
                >
                  {deleting ? (
                    <Loader2 className="size-4 animate-spin" aria-hidden />
                  ) : (
                    <Trash2 className="size-4" aria-hidden />
                  )}
                  <Trans>Delete</Trans>
                </Button>
                {hasReminders && (
                  <Button variant="outline" size="touch" onClick={() => onDownloadIcs(entry.id)}>
                    <Calendar className="size-4" aria-hidden />
                    <Trans>Add to calendar</Trans>
                  </Button>
                )}
              </div>
            </div>
          </m.div>
        )}
      </AnimatePresence>

      {/* Delete confirm — ResponsiveSheet instead of the native confirm() (P20). */}
      <ConfirmSheet
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title={<Trans>Delete this entry?</Trans>}
        description={<Trans>This cannot be undone.</Trans>}
        confirmLabel={<Trans>Delete</Trans>}
        icon={<Trash2 className="size-4" aria-hidden />}
        pending={deleting}
        onConfirm={async () => {
          // Close first: a successful delete unmounts this card.
          setConfirmDelete(false);
          setDeleting(true);
          await onDelete(entry.id);
          setDeleting(false);
        }}
      />
    </m.div>
  );
}

/* ─── Empty State ─── */

function EmptyJournal({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="border-border bg-card rounded-xl border p-5">
      <div className="flex flex-col items-center justify-center py-12">
        <div className="flex size-16 items-center justify-center rounded-full bg-teal-50 dark:bg-teal-950">
          <BookOpen className="size-8 text-teal-500" />
        </div>
        <h3 className="text-foreground mt-4 text-sm font-semibold">
          <Trans>Your journal is empty</Trans>
        </h3>
        <p className="text-muted-foreground mt-1 max-w-sm text-center text-sm">
          <Trans>Track visits, lab results, prescriptions and reminders all in one place.</Trans>
        </p>
        <Button className="mt-4 min-h-[44px] gap-1.5" onClick={onAdd}>
          <Plus className="size-4" />
          <Trans>Add your first entry</Trans>
        </Button>
      </div>
    </div>
  );
}

/* ─── Skeleton ─── */

function JournalSkeleton() {
  return (
    <div className="space-y-3">
      {[0, 1, 2].map((i) => (
        <div key={i} className="border-border bg-card animate-pulse rounded-xl border p-4">
          <div className="flex items-start gap-3">
            <div className="bg-muted size-10 rounded-lg" />
            <div className="flex-1 space-y-2">
              <div className="bg-muted h-4 w-48 rounded" />
              <div className="bg-muted h-3 w-32 rounded" />
              <div className="bg-muted h-3 w-64 rounded" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
